import { Line, Canvas, Rect, Image } from "fabric";
import { getCanvasStoreValues } from "../store/canvasStoreInstance";
import { getDisplaySize } from "../utils/resSize";

export type LayoutStream = "screen" | "webcam";

// Minimum size for webcam (16% of canvas as defined in DraggableResizableStream.tsx)
const MIN_SIZE_PCT = 0.16;

// Fit a target aspect inside a box (contain). Returns floored ints.
function fitAspect(aspect: number, availW: number, availH: number) {
  const a = aspect > 0 ? aspect : 16 / 9;
  let w = availW;
  let h = w / a;
  if (h > availH) {
    h = availH;
    w = h * a;
  }
  return { width: Math.floor(w), height: Math.floor(h) };
}

// Center a rect of (w,h) inside the canvas bleed (padding on each side).
function centerInBleed(
  w: number,
  h: number,
  canvasW: number,
  canvasH: number,
  padding: number
) {
  const availW = canvasW - padding * 2;
  const availH = canvasH - padding * 2;
  const x = Math.floor(padding + (availW - w) / 2);
  const y = Math.floor(padding + (availH - h) / 2);
  return { x, y };
}

// Optional small clamp
function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

const getMinWebcamSize = (
  canvasWidth: number,
  canvasHeight: number,
  roundedWebcam: boolean,
  canvasRatio: string = "16:9"
): number => {
  // Calculate minimum size based on the actual canvas dimensions (not scaled)
  const minSize = Math.min(canvasWidth, canvasHeight) * MIN_SIZE_PCT;

  // Calculate display scale factor based on canvas ratio
  // For portrait mode (9:16), the scale calculation is different
  let displayScale = 1;
  if (canvasRatio === "9:16") {
    // In portrait mode, the canvas is scaled differently
    // Use a conservative scale factor that ensures minimum size is reasonable
    displayScale = Math.min(1, 720 / Math.max(canvasWidth, canvasHeight));
  } else {
    // For landscape and square, use a standard scale factor
    displayScale = Math.min(1, 1280 / Math.max(canvasWidth, canvasHeight));
  }

  // Apply display scale to get the visible size
  const displayMinSize = minSize / displayScale;

  // Ensure minimum size is at least the current fixed values, adjusted for display
  const absoluteMinSize = Math.max(displayMinSize, roundedWebcam ? 100 : 160);

  // At the end of getMinWebcamSize, before return:
  const padding = getPadding(canvasRatio, canvasWidth, canvasHeight);
  const cap = Math.min(canvasWidth, canvasHeight) - padding * 2;
  return Math.min(absoluteMinSize, Math.max(0, cap));
};

export type TLayoutToFrameCallback = (
  canvas: Canvas,
  frame: Rect,
  videoImg: Image,
  clip: Rect,
  vw: number,
  vh: number,
  isScreen: boolean,
  isWebcam: boolean
) => void;
export interface StreamLayout {
  stream: LayoutStream;
  width: number;
  height: number;
  x: number;
  y: number;
  keepAspectRatio?: boolean;
}

export type TGuideLines = {
  left: Line | null;
  right: Line | null;
  top: Line | null;
  bottom: Line | null;
  centerX: Line | null;
  centerY: Line | null;
  // object alignment guides
  objectLeft: Line | null;
  objectRight: Line | null;
  objectTop: Line | null;
  objectBottom: Line | null;
  objectCenterX: Line | null;
  objectCenterY: Line | null;
  // canvas edges
  leftEdge: Line | null;
  rightEdge: Line | null;
  topEdge: Line | null;
  bottomEdge: Line | null;
  squareH: Line | null;
  squareV: Line | null;
};
export type LayoutPreset = StreamLayout[];

export type PresetFn = (
  canvasWidth: number,
  canvasHeight: number,
  screenAspectRatio: number,
  roundedWebcam: boolean,
  canvasRatio: string,
  edgeToEdge?: boolean
) => LayoutPreset;

export const getPadding = (canvasRatio: string, canvasWidth?: number, canvasHeight?: number): number => {
  // Use a fixed percentage of the smaller canvas dimension for uniform padding
  // This avoids relying on wrapperDimensions which can be stale during ratio transitions
  const PADDING_PERCENTAGE = 0.018; // 1.8% of smaller dimension
  
  if (canvasWidth && canvasWidth > 0) {
    const height = canvasHeight || canvasWidth;
    const smallerDimension = Math.min(canvasWidth, height);
    return Math.max(16, Math.min(48, Math.round(smallerDimension * PADDING_PERCENTAGE)));
  }

  // Fallback for calls without canvas dimensions
  return 24;
};

export const getInsets = (canvasRatio: string, edgeToEdge: boolean = false, canvasWidth?: number, canvasHeight?: number) => {
  const padding = getPadding(canvasRatio, canvasWidth, canvasHeight);
  if (edgeToEdge) {
    return {
      left: 0,
      top: 0,
      right: 0,
      bottom: 0
    };
  }
  return {
    left: padding,
    top: padding,
    right: padding,
    bottom: padding
  };
};

export const Preset_Free: PresetFn = () => [];

export const Preset_PresentationSlide: PresetFn = () => [];

export const Preset_PresentationSlideWithWebcam: PresetFn = (
  canvasWidth,
  canvasHeight,
  _screenAspectRatio,
  roundedWebcam,
  canvasRatio
) => {
  const padding = getPadding(canvasRatio, canvasWidth, canvasHeight);
  let webcamSize = Math.max(
    getMinWebcamSize(canvasWidth, canvasHeight, true, canvasRatio),
    100
  );
  webcamSize = Math.min(webcamSize, Math.min(canvasWidth, canvasHeight) * 0.25);

  const webcamX = canvasWidth - webcamSize - padding;
  const webcamY = canvasHeight - webcamSize - padding;

  return [
    {
      stream: "webcam" as LayoutStream,
      x: webcamX,
      y: webcamY,
      width: webcamSize,
      height: webcamSize,
      keepAspectRatio: false,
    },
  ];
};

export const Preset_WebcamFull: PresetFn = (
  canvasWidth,
  canvasHeight,
  _,
  roundedWebcam,
  canvasRatio,
  edgeToEdge = false
) => {
  const insets = getInsets(canvasRatio, edgeToEdge, canvasWidth, canvasHeight);

  let x, y, finalWidth, finalHeight;

  if (roundedWebcam) {
    // For rounded webcam, we want a square that fits within the padded area
    const availableWidth = canvasWidth - insets.left - insets.right;
    const availableHeight = canvasHeight - insets.top - insets.bottom;
    const minSize = Math.min(availableWidth, availableHeight);
    finalWidth = finalHeight = minSize;
    x = (canvasWidth - minSize) / 2;
    y = (canvasHeight - minSize) / 2;
  } else {
    // For rectangular webcam, fill the canvas with padding
    finalWidth = canvasWidth - insets.left - insets.right;
    finalHeight = canvasHeight - insets.top - insets.bottom;
    x = insets.left;
    y = insets.top;
  }

  // Debug logging disabled to reduce console bloat

  return [
    {
      stream: "webcam",
      x,
      y,
      width: finalWidth,
      height: finalHeight,
      keepAspectRatio: false,
    },
  ];
};

export const Preset_ScreenFull: PresetFn = (
  canvasWidth,
  canvasHeight,
  screenAspectRatio,
  _,
  canvasRatio,
  edgeToEdge = false
) => {
  const insets = getInsets(canvasRatio, edgeToEdge, canvasWidth, canvasHeight);
  const availW = canvasWidth - insets.left - insets.right;
  const availH = canvasHeight - insets.top - insets.bottom;
  const { width, height } = fitAspect(screenAspectRatio, availW, availH);
  const { x, y } = centerInBleed(
    width,
    height,
    canvasWidth,
    canvasHeight,
    insets.left
  );
  return [
    {
      stream: "screen",
      x,
      y,
      width,
      height,
      keepAspectRatio: true,
    },
  ];
};

const createMiniCamPreset = (
  w: number,
  h: number,
  screenAspectRatio: number,
  roundedWebcam: boolean,
  canvasRatio: string,
  webcamPosition: "top-left" | "top-right" | "bottom-left" | "bottom-right",
  edgeToEdge: boolean = false
): LayoutPreset => {
  const insets = getInsets(canvasRatio, edgeToEdge, w, h);
  const availW = w - insets.left - insets.right;
  const availH = h - insets.top - insets.bottom;
  const { width: screenWidth, height: screenHeight } = fitAspect(
    screenAspectRatio,
    availW,
    availH
  );
  const { x: screenX, y: screenY } = centerInBleed(
    screenWidth,
    screenHeight,
    w,
    h,
    insets.left
  );

  let webcamSize = Math.max(
    getMinWebcamSize(w, h, roundedWebcam, canvasRatio),
    roundedWebcam ? 100 : 160
  );

  // After computing webcamSize, cap it:
  webcamSize = Math.min(
    webcamSize,
    screenWidth,
    screenHeight,
    Math.min(availW, availH)
  );

  // For mini-cam presets, always use padding as the distance from screen edges even in edge-to-edge mode
  const webcamPadding = getPadding(canvasRatio, w, h);
  // Position webcam relative to the screen rect (not the canvas), then clamp to bleed:
  let webcamX = webcamPosition.includes("right")
    ? screenX + screenWidth - webcamSize - webcamPadding
    : screenX + webcamPadding;
  let webcamY = webcamPosition.includes("bottom")
    ? screenY + screenHeight - webcamSize - webcamPadding
    : screenY + webcamPadding;
  webcamX = clamp(webcamX, insets.left, w - insets.right - webcamSize);
  webcamY = clamp(webcamY, insets.top, h - insets.bottom - webcamSize);

  return [
    {
      stream: "screen",
      x: screenX,
      y: screenY,
      width: screenWidth,
      height: screenHeight,
      keepAspectRatio: true,
    },
    {
      stream: "webcam",
      x: webcamX,
      y: webcamY,
      width: webcamSize,
      height: webcamSize,
      keepAspectRatio: false,
    },
  ];
};

export const Preset_CamBottomRight = (
  w: number,
  h: number,
  r: number,
  rw: boolean,
  canvasRatio: string,
  edgeToEdge: boolean = false
) => createMiniCamPreset(w, h, r, rw, canvasRatio, "bottom-right", edgeToEdge);
export const Preset_CamBottomLeft = (
  w: number,
  h: number,
  r: number,
  rw: boolean,
  canvasRatio: string,
  edgeToEdge: boolean = false
) => createMiniCamPreset(w, h, r, rw, canvasRatio, "bottom-left", edgeToEdge);
export const Preset_CamTopRight = (
  w: number,
  h: number,
  r: number,
  rw: boolean,
  canvasRatio: string,
  edgeToEdge: boolean = false
) => createMiniCamPreset(w, h, r, rw, canvasRatio, "top-right", edgeToEdge);
export const Preset_CamTopLeft = (
  w: number,
  h: number,
  r: number,
  rw: boolean,
  canvasRatio: string,
  edgeToEdge: boolean = false
) => createMiniCamPreset(w, h, r, rw, canvasRatio, "top-left", edgeToEdge);

const Preset_Default: PresetFn = (
  wOrCanvasWidth,
  hOrCanvasHeight,
  screenAspectRatio,
  roundedWebcam,
  canvasRatio,
  edgeToEdge = false
) => {
  const insets = getInsets(canvasRatio, edgeToEdge, wOrCanvasWidth, hOrCanvasHeight);
  const padding = getPadding(canvasRatio, wOrCanvasWidth, hOrCanvasHeight);
  const availW = wOrCanvasWidth - insets.left - insets.right;
  const availH = hOrCanvasHeight - insets.top - insets.bottom;
  const innerWidth = wOrCanvasWidth - insets.left - insets.right - padding;
  const targetScreenColW = innerWidth * 0.75;
  const targetWebcamColW = Math.max(
    innerWidth * 0.25,
    getMinWebcamSize(
      wOrCanvasWidth,
      hOrCanvasHeight,
      roundedWebcam,
      canvasRatio
    )
  );

  // Screen
  const { width: screenW, height: screenH } = fitAspect(
    screenAspectRatio,
    targetScreenColW,
    availH
  );

  // Webcam
  let webcamW, webcamH;
  if (roundedWebcam) {
    webcamW = Math.min(targetWebcamColW, availH);
    webcamH = webcamW;
  } else {
    webcamW = targetWebcamColW;
    webcamH = screenH;
  }

  // For Preset_Default, also limit webcam height
  webcamH = Math.min(webcamH, Math.floor(availH / 2));
  if (roundedWebcam) webcamW = webcamH;

  // Group - preserve inner gap even in edge-to-edge mode for side-by-side layouts
  const innerGap = padding;
  const groupW = screenW + innerGap + webcamW;
  const groupH = Math.max(screenH, webcamH);
  const groupX = insets.left + Math.floor((availW - groupW) / 2);
  const groupY = insets.top + Math.floor((availH - groupH) / 2);

  return [
    {
      stream: "screen",
      x: groupX,
      y: groupY,
      width: screenW,
      height: screenH,
      keepAspectRatio: true,
    },
    {
      stream: "webcam",
      x: groupX + screenW + innerGap,
      y: groupY,
      width: webcamW,
      height: webcamH,
      keepAspectRatio: false,
    },
  ];
};

const Preset_Inverted: PresetFn = (
  wOrCanvasWidth,
  hOrCanvasHeight,
  screenAspectRatio,
  roundedWebcam,
  canvasRatio,
  edgeToEdge = false
) => {
  const insets = getInsets(canvasRatio, edgeToEdge, wOrCanvasWidth, hOrCanvasHeight);
  const padding = getPadding(canvasRatio, wOrCanvasWidth, hOrCanvasHeight);
  const availW = wOrCanvasWidth - insets.left - insets.right;
  const availH = hOrCanvasHeight - insets.top - insets.bottom;
  const innerWidth = wOrCanvasWidth - insets.left - insets.right - padding;
  const targetScreenColW = innerWidth * 0.75;
  const targetWebcamColW = Math.max(
    innerWidth * 0.25,
    getMinWebcamSize(
      wOrCanvasWidth,
      hOrCanvasHeight,
      roundedWebcam,
      canvasRatio
    )
  );

  // Screen
  const { width: screenW, height: screenH } = fitAspect(
    screenAspectRatio,
    targetScreenColW,
    availH
  );

  // Webcam
  let webcamW, webcamH;
  if (roundedWebcam) {
    webcamW = Math.min(targetWebcamColW, availH);
    webcamH = webcamW;
  } else {
    webcamW = targetWebcamColW;
    webcamH = screenH;
  }

  // For Preset_Inverted, also limit webcam height
  webcamH = Math.min(webcamH, Math.floor(availH / 2));
  if (roundedWebcam) webcamW = webcamH;

  // Group - preserve inner gap even in edge-to-edge mode for side-by-side layouts
  const innerGap = padding;
  const groupW = webcamW + innerGap + screenW;
  const groupH = Math.max(screenH, webcamH);
  const groupX = insets.left + Math.floor((availW - groupW) / 2);
  const groupY = insets.top + Math.floor((availH - groupH) / 2);

  return [
    {
      stream: "screen",
      x: groupX + webcamW + innerGap,
      y: groupY,
      width: screenW,
      height: screenH,
      keepAspectRatio: true,
    },
    {
      stream: "webcam",
      x: groupX,
      y: groupY,
      width: webcamW,
      height: webcamH,
      keepAspectRatio: false,
    },
  ];
};

const Preset_TopRightCam = Preset_CamTopRight;
const Preset_TopLeftCam = Preset_CamTopLeft;
const Preset_BottomRightMiniCam = Preset_CamBottomRight;
const Preset_BottomLeftMiniCam = Preset_CamBottomLeft;

const Preset_SideBySide_RightCam: PresetFn = (
  w,
  h,
  screenAspectRatio,
  roundedWebcam,
  canvasRatio,
  edgeToEdge = false
) => {
  const insets = getInsets(canvasRatio, edgeToEdge, w, h);
  const padding = getPadding(canvasRatio, w, h);
  const availW = w - insets.left - insets.right;
  const availH = h - insets.top - insets.bottom;
  const innerWidth = w - insets.left - insets.right - padding;
  const targetScreenColW = innerWidth * 0.75;
  const targetWebcamColW = Math.max(
    innerWidth * 0.25,
    getMinWebcamSize(w, h, roundedWebcam, canvasRatio)
  );

  // Screen
  const { width: screenW, height: screenH } = fitAspect(
    screenAspectRatio,
    targetScreenColW,
    availH
  );

  // Webcam
  let webcamW, webcamH;
  if (roundedWebcam) {
    webcamW = Math.min(targetWebcamColW, availH);
    webcamH = webcamW;
  } else {
    webcamW = targetWebcamColW;
    webcamH = screenH;
  }

  // Group - preserve inner gap even in edge-to-edge mode for side-by-side layouts
  const innerGap = padding;
  const groupW = screenW + innerGap + webcamW;
  const groupH = Math.max(screenH, webcamH);
  const groupX = insets.left + Math.floor((availW - groupW) / 2);
  const groupY = insets.top + Math.floor((availH - groupH) / 2);

  return [
    {
      stream: "screen",
      x: groupX,
      y: groupY,
      width: screenW,
      height: screenH,
      keepAspectRatio: true,
    },
    {
      stream: "webcam",
      x: groupX + screenW + innerGap,
      y: groupY,
      width: webcamW,
      height: webcamH,
      keepAspectRatio: false,
    },
  ];
};

const Preset_SideBySide_LeftCam: PresetFn = (
  w,
  h,
  screenAspectRatio,
  roundedWebcam,
  canvasRatio,
  edgeToEdge = false
) => {
  const insets = getInsets(canvasRatio, edgeToEdge, w, h);
  const padding = getPadding(canvasRatio, w, h);
  const availW = w - insets.left - insets.right;
  const availH = h - insets.top - insets.bottom;
  const innerWidth = w - insets.left - insets.right - padding;
  const targetScreenColW = innerWidth * 0.75;
  const targetWebcamColW = Math.max(
    innerWidth * 0.25,
    getMinWebcamSize(w, h, roundedWebcam, canvasRatio)
  );

  // Screen
  const { width: screenW, height: screenH } = fitAspect(
    screenAspectRatio,
    targetScreenColW,
    availH
  );

  // Webcam
  let webcamW, webcamH;
  if (roundedWebcam) {
    webcamW = Math.min(targetWebcamColW, availH);
    webcamH = webcamW;
  } else {
    webcamW = targetWebcamColW;
    webcamH = screenH;
  }

  // Group - preserve inner gap even in edge-to-edge mode for side-by-side layouts
  const innerGap = padding;
  const groupW = webcamW + innerGap + screenW;
  const groupH = Math.max(screenH, webcamH);
  const groupX = insets.left + Math.floor((availW - groupW) / 2);
  const groupY = insets.top + Math.floor((availH - groupH) / 2);

  return [
    {
      stream: "screen",
      x: groupX + webcamW + innerGap,
      y: groupY,
      width: screenW,
      height: screenH,
      keepAspectRatio: true,
    },
    {
      stream: "webcam",
      x: groupX,
      y: groupY,
      width: webcamW,
      height: webcamH,
      keepAspectRatio: false,
    },
  ];
};

const Preset_SideCamStacked: PresetFn = (
  w,
  h,
  screenAspectRatio,
  roundedWebcam,
  canvasRatio,
  edgeToEdge = false
) => {
  const insets = getInsets(canvasRatio, edgeToEdge, w, h);
  const padding = getPadding(canvasRatio, w, h);
  const availW = w - insets.left - insets.right;
  const availH = h - insets.top - insets.bottom;
  const { width: screenW, height: screenH } = fitAspect(
    screenAspectRatio,
    availW,
    availH
  );

  let webcamH = roundedWebcam
    ? Math.min(screenW, availH - screenH - padding)
    : Math.min(screenH, availH - screenH - padding);
  let webcamW = roundedWebcam ? webcamH : screenW;

  // enforce min, but cap to remaining space
  const minCam = getMinWebcamSize(w, h, roundedWebcam, canvasRatio);
  if (roundedWebcam) {
    webcamH = webcamW = Math.min(
      Math.max(minCam, webcamW),
      availH - screenH - padding
    );
  } else {
    webcamH = Math.min(Math.max(minCam, webcamH), availH - screenH - padding);
  }

  const stackH = screenH + padding + webcamH;
  const groupX = insets.left + Math.floor((availW - screenW) / 2);
  const groupY = insets.top + Math.floor((availH - stackH) / 2);

  return [
    {
      stream: "screen",
      x: groupX,
      y: groupY,
      width: screenW,
      height: screenH,
      keepAspectRatio: true,
    },
    {
      stream: "webcam",
      x: groupX,
      y: groupY + screenH + padding,
      width: webcamW,
      height: webcamH,
      keepAspectRatio: false,
    },
  ];
};

const Preset_SideCamStackedInverted: PresetFn = (
  w,
  h,
  screenAspectRatio,
  roundedWebcam,
  canvasRatio,
  edgeToEdge = false
) => {
  const insets = getInsets(canvasRatio, edgeToEdge, w, h);
  const padding = getPadding(canvasRatio, w, h);
  const availW = w - insets.left - insets.right;
  const availH = h - insets.top - insets.bottom;
  const { width: screenW, height: screenH } = fitAspect(
    screenAspectRatio,
    availW,
    availH
  );

  let webcamH = roundedWebcam
    ? Math.min(screenW, availH - screenH - padding)
    : Math.min(screenH, availH - screenH - padding);
  let webcamW = roundedWebcam ? webcamH : screenW;

  // enforce min, but cap to remaining space
  const minCam = getMinWebcamSize(w, h, roundedWebcam, canvasRatio);
  if (roundedWebcam) {
    webcamH = webcamW = Math.min(
      Math.max(minCam, webcamW),
      availH - screenH - padding
    );
  } else {
    webcamH = Math.min(Math.max(minCam, webcamH), availH - screenH - padding);
  }

  const stackH = webcamH + padding + screenH;
  const groupX = insets.left + Math.floor((availW - screenW) / 2);
  const groupY = insets.top + Math.floor((availH - stackH) / 2);

  return [
    {
      stream: "screen",
      x: groupX,
      y: groupY + webcamH + padding,
      width: screenW,
      height: screenH,
      keepAspectRatio: true,
    },
    {
      stream: "webcam",
      x: groupX,
      y: groupY,
      width: webcamW,
      height: webcamH,
      keepAspectRatio: false,
    },
  ];
};

export const landscapePresets = {
  Preset_Default,
  Preset_Inverted,
  Preset_TopRightCam,
  Preset_TopLeftCam,
  Preset_SideBySide_RightCam,
  Preset_SideBySide_LeftCam,
  Preset_BottomRightMiniCam,
  Preset_BottomLeftMiniCam,
};

export const squarePresets = {
  Preset_CamBottomRight,
  Preset_CamBottomLeft,
  Preset_CamTopRight,
  Preset_CamTopLeft,
};

export const portraitPresets = {
  Preset_SideCamStacked,
  Preset_SideCamStackedInverted,
};
